import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Avatar, Chip,
  ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Switch, FormControlLabel, LinearProgress, Divider,
  CircularProgress, Button, Stack, TextField, InputAdornment, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useTheme,
  CardActions
} from '@mui/material';
import {
  Receipt as AttendanceIcon, CheckCircle as PresentIcon, Cancel as AbsentIcon,
  AccessTime as LateIcon, Info as ExcusedIcon, Warning as WarningIcon,
  QrCodeScanner as QrCodeScannerIcon, Refresh as RefreshIcon, Search as SearchIcon,
  DoneAll as DoneAllIcon, School as SchoolIcon, MeetingRoom as RoomIcon,
  Person as TeacherIcon, GridView as GridViewIcon, FormatListBulleted as TableListIcon,
  FilterList as FilterIcon, Event as EventIcon, Today as TodayIcon,
  Description as DescriptionIcon, AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon, EditNote as EditNoteIcon,
  Download as DownloadIcon, CloudUpload as CloudUploadIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { QrAttendanceScannerModal } from '../components/QrAttendanceScannerModal';
import { academyStore } from '../services/academyStore';
import { academyApi, toLocalDateStr } from '../services/academyApi';
import { Attendance, AttendanceStatus, Session, Student } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';

export const AttendancePage: React.FC = () => {
  const { t, language } = useLanguage();
  const theme = useTheme();
  const { user } = useAuth();

  const [, setTick] = useState(0);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [teacherViewOnly, setTeacherViewOnly] = useState(false);
  const [dateFilter, setDateFilter] = useState<'today' | 'all'>('today');
  const [sessionSearch, setSessionSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | AttendanceStatus | 'ABSENT_JUSTIFIED' | 'ABSENT_UNJUSTIFIED'>('ALL');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  const [holdAlert, setHoldAlert] = useState<string | null>(null);
  const [savingStudentId, setSavingStudentId] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Justification Dialog State
  const [justificationModalOpen, setJustificationModalOpen] = useState(false);
  const [selectedStudentForJustification, setSelectedStudentForJustification] = useState<Student | null>(null);
  const [selectedAttendanceForJustification, setSelectedAttendanceForJustification] = useState<Attendance | null>(null);
  const [justificationReasonInput, setJustificationReasonInput] = useState('');
  const [justificationDocNameInput, setJustificationDocNameInput] = useState('');
  const [justificationDocBase64Input, setJustificationDocBase64Input] = useState('');
  const [justificationSaving, setJustificationSaving] = useState(false);

  // QR Scanner Modal State
  const [scannerOpen, setScannerOpen] = useState(false);

  // Batch mark dialog
  const [confirmMarkAllOpen, setConfirmMarkAllOpen] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);

  useEffect(() => {
    return academyStore.subscribe(() => setTick(t => t + 1));
  }, []);

  const sessions = academyStore.getSessions();
  const groups = academyStore.getGroups();
  const courses = academyStore.getCourses();
  const rooms = academyStore.getRooms();
  const teachers = academyStore.getTeachers();
  const students = academyStore.getStudents();
  const groupStudents = academyStore.getGroupStudents();
  const attendances = academyStore.getAttendances();

  const todayStr = toLocalDateStr(new Date());

  // Filtered Sessions
  const filteredSessions = useMemo(() => {
    let list = sessions;

    // Filter teacher sessions if user is teacher or toggle enabled
    if (teacherViewOnly) {
      const userTeacher = teachers.find(tch => tch.email && user?.email && tch.email.toLowerCase() === user.email.toLowerCase());
      const teacherId = userTeacher ? userTeacher.id : 1;
      list = list.filter(s => s.teacher_id === teacherId);
    }

    // Filter date
    if (dateFilter === 'today') {
      list = list.filter(s => s.date === todayStr);
    }

    // Filter search text
    if (sessionSearch.trim()) {
      const q = sessionSearch.toLowerCase();
      list = list.filter(s => {
        const grp = groups.find(g => g.id === s.group_id);
        const crs = grp ? courses.find(c => c.id === grp.course_id) : null;
        const rm = rooms.find(r => r.id === s.room_id);
        const tch = teachers.find(t => t.id === s.teacher_id);
        return (
          (grp?.name && grp.name.toLowerCase().includes(q)) ||
          (crs?.title && crs.title.toLowerCase().includes(q)) ||
          (rm?.room_number && rm.room_number.toLowerCase().includes(q)) ||
          (tch?.full_name && tch.full_name.toLowerCase().includes(q)) ||
          (s.date && s.date.includes(q))
        );
      });
    }

    return list.slice().sort((a, b) => {
      const cmpDate = a.date.localeCompare(b.date);
      if (cmpDate !== 0) return cmpDate;
      return (a.start_time || '').localeCompare(b.start_time || '');
    });
  }, [sessions, groups, courses, rooms, teachers, teacherViewOnly, dateFilter, sessionSearch, todayStr, user]);

  useEffect(() => {
    if (filteredSessions.length > 0) {
      if (!selectedSessionId || !filteredSessions.some(s => s.id === selectedSessionId)) {
        setSelectedSessionId(filteredSessions[0].id);
      }
    } else if (sessions.length > 0 && dateFilter === 'today') {
      // If no sessions today, fallback to all sessions if user toggles
    }
  }, [filteredSessions, selectedSessionId, sessions.length, dateFilter]);

  const currentSession = selectedSessionId
    ? sessions.find(s => s.id === selectedSessionId) ?? null
    : filteredSessions[0] ?? null;

  const currentGroup = currentSession ? groups.find(g => g.id === currentSession.group_id) : null;
  const currentCourse = currentGroup ? courses.find(c => c.id === currentGroup.course_id) : null;
  const currentRoom = currentSession ? rooms.find(r => r.id === currentSession.room_id) : null;
  const currentTeacher = currentSession ? teachers.find(t => t.id === currentSession.teacher_id) : null;

  // Enrolled students in current session
  const enrolledStudentIds = currentSession
    ? groupStudents
      .filter(gs => gs.group_id === currentSession.group_id && gs.status === 'Enrolled')
      .map(gs => gs.student_id)
    : [];

  const sessionStudents = students.filter(s => enrolledStudentIds.includes(s.id));

  // Filtered session students by search & status
  const visibleStudents = useMemo(() => {
    return sessionStudents.filter(student => {
      // Search
      if (studentSearch.trim()) {
        const q = studentSearch.toLowerCase();
        const studentCode = (student.code || student.STD_CODE || `STD-${student.id}`).toLowerCase();
        const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
        if (!fullName.includes(q) && !studentCode.includes(q) && !(student.phone && student.phone.includes(q))) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'ALL' && currentSession) {
        const att = attendances.find(a => a.session_id === currentSession.id && a.student_id === student.id);
        const st = att?.status;
        if (statusFilter === 'ABSENT_JUSTIFIED') {
          if (st !== 'Absent' && st !== 'Excused') return false;
          if (!att?.justified) return false;
        } else if (statusFilter === 'ABSENT_UNJUSTIFIED') {
          if (st !== 'Absent') return false;
          if (att?.justified) return false;
        } else if (st !== statusFilter) {
          return false;
        }
      }

      return true;
    });
  }, [sessionStudents, studentSearch, statusFilter, currentSession, attendances]);

  // Statistics calculation for current session
  const stats = useMemo(() => {
    if (!currentSession) return { total: 0, present: 0, late: 0, absent: 0, absentJustified: 0, absentUnjustified: 0, excused: 0, unmarked: 0, rate: 0 };

    const total = sessionStudents.length;
    let present = 0;
    let late = 0;
    let absent = 0;
    let absentJustified = 0;
    let absentUnjustified = 0;
    let excused = 0;

    sessionStudents.forEach(st => {
      const att = attendances.find(a => a.session_id === currentSession.id && a.student_id === st.id);
      if (att?.status === 'Present') present++;
      else if (att?.status === 'Late') late++;
      else if (att?.status === 'Absent') {
        absent++;
        if (att?.justified) absentJustified++;
        else absentUnjustified++;
      }
      else if (att?.status === 'Excused') {
        excused++;
        if (att?.justified) absentJustified++;
      }
    });

    const marked = present + late + absent + excused;
    const unmarked = Math.max(0, total - marked);
    const rate = total > 0 ? Math.round(((present + late) / total) * 100) : 0;

    return { total, present, late, absent, absentJustified, absentUnjustified, excused, unmarked, rate };
  }, [currentSession, sessionStudents, attendances]);

  // Open Justification Dialog
  const handleOpenJustification = (student: Student, att?: Attendance | null) => {
    setSelectedStudentForJustification(student);
    setSelectedAttendanceForJustification(att || null);
    setJustificationReasonInput(att?.justification_reason || '');
    setJustificationDocNameInput(att?.justification_doc_name || '');
    setJustificationDocBase64Input(att?.justification_doc_base64 || '');
    setJustificationModalOpen(true);
  };

  // Save Justification
  const handleSaveJustification = async () => {
    if (!currentSession || !selectedStudentForJustification) return;
    if (!justificationReasonInput.trim()) {
      blobticMessage.error(t('common.error'), t('academy.attendance.reason_label') || 'Veuillez saisir un motif.');
      return;
    }
    setJustificationSaving(true);
    try {
      const res = await academyStore.saveAttendanceJustification(
        currentSession.id,
        selectedStudentForJustification.id,
        justificationReasonInput.trim(),
        justificationDocNameInput,
        justificationDocBase64Input
      );
      if (res.success) {
        blobticMessage.success(t('common.messages.success'), t('academy.attendance.justification_saved'));
        setJustificationModalOpen(false);
      } else {
        blobticMessage.error(t('common.error'), res.message || 'Erreur lors de l\'enregistrement.');
      }
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err?.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setJustificationSaving(false);
    }
  };

  // Download / View Document
  const handleDownloadDoc = (base64: string, fileName: string) => {
    if (!base64) return;
    let href = base64;
    if (!href.startsWith('data:')) {
      const lowerName = (fileName || '').toLowerCase();
      let mime = 'application/octet-stream';
      if (lowerName.endsWith('.pdf')) mime = 'application/pdf';
      else if (lowerName.endsWith('.png')) mime = 'image/png';
      else if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) mime = 'image/jpeg';
      href = `data:${mime};base64,${base64}`;
    }
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName || 'justificatif';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // File Upload Handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result as string;
      setJustificationDocBase64Input(res);
      setJustificationDocNameInput(file.name);
    };
    reader.readAsDataURL(file);
  };

  // Refresh data from server
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        academyStore._refresh('academy_session', () => academyApi.getSessions()),
        academyStore._refresh('academy_attendance', () => academyApi.getAttendances()),
        academyStore._refresh('academy_student', () => academyApi.getStudents()),
        academyStore._refresh('academy_group_student', () => academyApi.getGroupStudents()),
        academyStore._refresh('academy_group', () => academyApi.getGroups()),
      ]);
      setTick(t => t + 1);
      blobticMessage.success(t('common.messages.success'), t('common.refreshed') || 'Données actualisées avec succès.');
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err?.message || 'Erreur lors de l\'actualisation.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Toggle single attendance status
  const handleStatusToggle = async (studentId: number, status: AttendanceStatus) => {
    if (!currentSession || savingStudentId !== null) return;
    setHoldAlert(null);
    setSavingStudentId(studentId);

    try {
      const res = await academyStore.markAttendance(currentSession.id, studentId, status);
      if (!res.success) {
        setHoldAlert(res.message || t('academy.attendance.error.hold_block'));
        blobticMessage.error(t('common.error'), res.message || t('academy.attendance.error.hold_block'));
      }
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err?.message || 'Erreur lors du pointage.');
    } finally {
      setSavingStudentId(null);
    }
  };

  // Batch Mark All Present
  const handleConfirmMarkAll = async () => {
    if (!currentSession) return;
    setBatchLoading(true);
    try {
      for (const st of sessionStudents) {
        if (!st.financial_hold) {
          await academyStore.markAttendance(currentSession.id, st.id, 'Present');
        }
      }
      setConfirmMarkAllOpen(false);
      blobticMessage.success(t('common.messages.success'), `${sessionStudents.length} étudiants marqués présents.`);
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err?.message || 'Erreur lors du pointage global.');
    } finally {
      setBatchLoading(false);
    }
  };

  // QR Code Scanned Handler
  const handleStudentScanned = async (scannedStudent: Student): Promise<{ success: boolean; message?: string }> => {
    if (!currentSession) {
      return { success: false, message: t('academy.attendance.select_session_hint') || 'Aucune session sélectionnée.' };
    }

    // Check if student is enrolled in current session
    const isEnrolled = enrolledStudentIds.includes(scannedStudent.id);
    if (!isEnrolled) {
      // Find if student belongs to another session today
      const otherSessionToday = sessions.find(s => s.date === todayStr && groupStudents.some(gs => gs.group_id === s.group_id && gs.student_id === scannedStudent.id && gs.status === 'Enrolled'));
      if (otherSessionToday) {
        const otherGrp = groups.find(g => g.id === otherSessionToday.group_id);
        return {
          success: false,
          message: `Inscrit dans la session de ${otherSessionToday.start_time} (${otherGrp?.name || 'Autre groupe'})`
        };
      }
      return {
        success: false,
        message: t('academy.attendance.student_not_enrolled', `${scannedStudent.first_name} ${scannedStudent.last_name}`)
      };
    }

    // Mark as present
    const res = await academyStore.markAttendance(currentSession.id, scannedStudent.id, 'Present');
    if (res.success) {
      return {
        success: true,
        message: t('academy.attendance.mark_present_success', `${scannedStudent.first_name} ${scannedStudent.last_name}`)
      };
    } else {
      return {
        success: false,
        message: res.message || t('academy.attendance.error.hold_block')
      };
    }
  };

  const calculateStudentPassRate = (studentId: number) => {
    const studentAtts = attendances.filter(a => a.student_id === studentId);
    if (studentAtts.length === 0) return 100;
    const presentCount = studentAtts.filter(a => a.status === 'Present' || a.status === 'Late').length;
    return Math.round((presentCount / studentAtts.length) * 100);
  };

  return (
    <GenericPage
      title={t('academy.attendance.title') || 'Pointage & Feuilles de Présence'}
      icon={<AttendanceIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            startIcon={<QrCodeScannerIcon />}
            onClick={() => setScannerOpen(true)}
            sx={{ borderRadius: 2, fontWeight: 700, px: 2.5 }}
          >
            {t('academy.attendance.scan_qr') || 'Scanner Présence QR'}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={isRefreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{ borderRadius: 2 }}
          >
            {t('common.refresh') || 'Actualiser'}
          </Button>

          <FormControlLabel
            control={
              <Switch
                checked={teacherViewOnly}
                onChange={(e) => setTeacherViewOnly(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                {t('academy.attendance.teacher_mode') || 'Mes Sessions'}
              </Typography>
            }
            sx={{ m: 0, pl: 1, borderLeft: '1px solid', borderColor: 'divider' }}
          />
        </Stack>
      }
    >
      <Box sx={{ pb: 4 }}>
        {holdAlert && (
          <Alert severity="error" icon={<WarningIcon />} onClose={() => setHoldAlert(null)} sx={{ mb: 3, borderRadius: 2 }}>
            {holdAlert}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column: Sessions List & Filters */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={0} variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('academy.attendance.all_sessions') || 'Sessions'}
                </Typography>
                <Chip label={`${filteredSessions.length}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
              </Box>

              {/* Date Filter Tabs */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  size="small"
                  variant={dateFilter === 'today' ? 'contained' : 'outlined'}
                  startIcon={<TodayIcon />}
                  onClick={() => setDateFilter('today')}
                  sx={{ borderRadius: 2, flex: 1 }}
                >
                  Aujourd'hui
                </Button>
                <Button
                  size="small"
                  variant={dateFilter === 'all' ? 'contained' : 'outlined'}
                  startIcon={<EventIcon />}
                  onClick={() => setDateFilter('all')}
                  sx={{ borderRadius: 2, flex: 1 }}
                >
                  Toutes
                </Button>
              </Stack>

              {/* Session Search */}
              <TextField
                size="small"
                fullWidth
                value={sessionSearch}
                onChange={(e) => setSessionSearch(e.target.value)}
                placeholder="Rechercher groupe, salle, prof..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              {/* Sessions List */}
              <Stack spacing={1.5} sx={{ maxHeight: 'calc(100vh - 380px)', overflowY: 'auto', pr: 0.5 }}>
                {filteredSessions.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {dateFilter === 'today' ? "Aucune session prévue aujourd'hui." : (t('academy.attendance.no_sessions') || 'Aucune session trouvée.')}
                    </Typography>
                    {dateFilter === 'today' && sessions.length > 0 && (
                      <Button size="small" variant="text" onClick={() => setDateFilter('all')} sx={{ mt: 1 }}>
                        Voir toutes les sessions ({sessions.length})
                      </Button>
                    )}
                  </Paper>
                ) : (
                  filteredSessions.map(session => {
                    const grp = groups.find(g => g.id === session.group_id);
                    const crs = grp ? courses.find(c => c.id === grp.course_id) : null;
                    const rm = rooms.find(r => r.id === session.room_id);
                    const tch = teachers.find(t => t.id === session.teacher_id);
                    const isSelected = currentSession?.id === session.id;

                    const enrIds = groupStudents.filter(gs => gs.group_id === session.group_id && gs.status === 'Enrolled').map(gs => gs.student_id);
                    const sessionAtts = attendances.filter(a => a.session_id === session.id);
                    const presentCount = sessionAtts.filter(a => a.status === 'Present' || a.status === 'Late').length;
                    const progressPct = enrIds.length > 0 ? Math.round((presentCount / enrIds.length) * 100) : 0;

                    return (
                      <Card
                        key={session.id}
                        onClick={() => {
                          setSelectedSessionId(session.id);
                          setHoldAlert(null);
                        }}
                        variant="outlined"
                        sx={{
                          borderRadius: 2.5,
                          cursor: 'pointer',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderWidth: isSelected ? 2 : 1,
                          bgcolor: isSelected ? 'action.selected' : 'background.paper',
                          transition: 'all 0.2s',
                          boxShadow: isSelected ? 3 : 0,
                          '&:hover': { boxShadow: 2 }
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Chip
                              label={`${session.start_time || ''} - ${session.end_time || ''}`}
                              color={isSelected ? 'primary' : 'default'}
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                            <Chip
                              label={session.attendance_taken ? (t('academy.attendance.taken') || 'Pointé') : (t('academy.attendance.pending') || 'En attente')}
                              color={session.attendance_taken ? 'success' : 'warning'}
                              size="small"
                              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                            />
                          </Box>

                          <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {grp?.name || 'Groupe'}
                          </Typography>

                          {crs?.title && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                              {crs.title}
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <RoomIcon sx={{ fontSize: 14 }} /> {rm?.room_number || 'Salle'}
                            </Box>
                            {tch?.full_name && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
                                <TeacherIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption" noWrap sx={{ fontSize: '0.75rem', color: 'inherit' }}>
                                  {tch.full_name}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Progress Bar */}
                          <Box sx={{ mt: 1.2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem' }} color="text.secondary">
                                Présence : {presentCount}/{enrIds.length}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700 }} color="primary.main">
                                {progressPct}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progressPct}
                              color={progressPct === 100 ? 'success' : 'primary'}
                              sx={{ height: 5, borderRadius: 3 }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            </Paper>
          </Grid>

          {/* Right Column: Attendance Management Roster */}
          <Grid item xs={12} lg={8}>
            {currentSession && currentGroup ? (
              <Stack spacing={2.5}>
                {/* Session Header Card */}
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="h5" fontWeight={800}>
                          {currentGroup.name}
                        </Typography>
                        {currentCourse?.title && (
                          <Chip label={currentCourse.title} size="small" variant="outlined" color="primary" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {currentSession.date} • {currentSession.start_time} - {currentSession.end_time} • {currentRoom?.room_number} ({currentRoom?.floor}) • {currentTeacher?.full_name || '—'}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<DoneAllIcon />}
                        onClick={() => setConfirmMarkAllOpen(true)}
                        disabled={sessionStudents.length === 0}
                        sx={{ borderRadius: 2 }}
                      >
                        {t('academy.attendance.mark_all_present') || 'Tout Marquer Présent'}
                      </Button>

                      <ToggleButtonGroup
                        size="small"
                        value={viewMode}
                        exclusive
                        onChange={(_, next) => next && setViewMode(next)}
                      >
                        <ToggleButton value="grid">
                          <GridViewIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="table">
                          <TableListIcon fontSize="small" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Box>

                  {/* Summary KPI Metric Cards */}
                  <Grid container spacing={1.5} sx={{ mt: 2 }}>
                    <Grid item xs={6} sm={2.4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Inscrits</Typography>
                        <Typography variant="h6" fontWeight={800} color="primary.main">{stats.total}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: 'success.50', borderColor: 'success.light' }}>
                        <Typography variant="caption" color="success.dark" fontWeight={600}>{t('academy.attendance.stats_present') || 'Présents'}</Typography>
                        <Typography variant="h6" fontWeight={800} color="success.main">{stats.present}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: 'warning.50', borderColor: 'warning.light' }}>
                        <Typography variant="caption" color="warning.dark" fontWeight={600}>{t('academy.attendance.stats_late') || 'Retards'}</Typography>
                        <Typography variant="h6" fontWeight={800} color="warning.main">{stats.late}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: 'error.50', borderColor: 'error.light' }}>
                        <Typography variant="caption" color="error.dark" fontWeight={600}>{t('academy.attendance.stats_absent') || 'Absents'}</Typography>
                        <Typography variant="h6" fontWeight={800} color="error.main">{stats.absent}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: 'info.50', borderColor: 'info.light' }}>
                        <Typography variant="caption" color="info.dark" fontWeight={600}>{t('academy.attendance.stats_excused') || 'Excusés'}</Typography>
                        <Typography variant="h6" fontWeight={800} color="info.main">{stats.excused}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Filters & Roster Toolbar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                  <TextField
                    size="small"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    placeholder={t('academy.attendance.search_student') || 'Rechercher étudiant...'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      )
                    }}
                    sx={{ minWidth: 240 }}
                  />

                  <ToggleButtonGroup
                    size="small"
                    value={statusFilter}
                    exclusive
                    onChange={(_, next) => next && setStatusFilter(next)}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    <ToggleButton value="ALL">{t('academy.attendance.filter_all')} ({stats.total})</ToggleButton>
                    <ToggleButton value="Present">{t('academy.attendance.filter_present')} ({stats.present})</ToggleButton>
                    <ToggleButton value="Late">{t('academy.attendance.filter_late')} ({stats.late})</ToggleButton>
                    <ToggleButton value="Absent">{t('academy.attendance.filter_absent')} ({stats.absent})</ToggleButton>
                    <ToggleButton value="ABSENT_JUSTIFIED">{t('academy.attendance.filter_absent_justified')} ({stats.absentJustified})</ToggleButton>
                    <ToggleButton value="ABSENT_UNJUSTIFIED">{t('academy.attendance.filter_absent_unjustified')} ({stats.absentUnjustified})</ToggleButton>
                    <ToggleButton value="Excused">{t('academy.attendance.filter_excused')} ({stats.excused})</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Student Attendance List */}
                {sessionStudents.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    {t('academy.attendance.no_students') || "Aucun étudiant inscrit dans ce groupe."}
                  </Alert>
                ) : visibleStudents.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <Typography color="text.secondary">Aucun étudiant ne correspond aux critères de filtre.</Typography>
                  </Paper>
                ) : viewMode === 'grid' ? (
                  /* Grid Display */
                  <Grid container spacing={2}>
                    {visibleStudents.map(student => {
                      const att = attendances.find(a => a.session_id === currentSession.id && a.student_id === student.id);
                      const currentStatus = att?.status ?? null;
                      const passRate = calculateStudentPassRate(student.id);
                      const isSaving = savingStudentId === student.id;
                      const studentCode = student.code || student.STD_CODE || `STD-${String(student.id).padStart(4, '0')}`;

                      return (
                        <Grid item xs={12} sm={6} key={student.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 3,
                              borderColor: student.financial_hold ? 'error.light' : (currentStatus === 'Present' ? 'success.main' : 'divider'),
                              bgcolor: currentStatus === 'Present' ? 'action.hover' : 'background.paper',
                              transition: 'all 0.2s',
                              '&:hover': { boxShadow: 3 }
                            }}
                          >
                            <CardContent sx={{ pb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar
                                    src={student.photo_base64}
                                    sx={{
                                      width: 44,
                                      height: 44,
                                      bgcolor: student.financial_hold ? 'error.main' : 'primary.main',
                                      fontWeight: 700
                                    }}
                                  >
                                    {!student.photo_base64 && `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                                      {student.first_name} {student.last_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {studentCode}
                                    </Typography>
                                  </Box>
                                </Box>

                                {student.financial_hold ? (
                                  <Chip label={t('academy.students.hold_badge')} color="error" size="small" icon={<WarningIcon />} sx={{ height: 20, fontSize: '0.65rem' }} />
                                ) : (
                                  <Chip
                                    label={`${passRate}% assiduité`}
                                    color={passRate >= 75 ? 'success' : 'warning'}
                                    variant="outlined"
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                  />
                                )}
                              </Box>

                              {/* Interactive Attendance Toggle Group */}
                              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                {isSaving ? (
                                  <CircularProgress size={24} sx={{ my: 1 }} />
                                ) : (
                                  <ToggleButtonGroup
                                    size="small"
                                    value={currentStatus}
                                    exclusive
                                    disabled={isSaving}
                                    onChange={(_, newStatus) => newStatus && handleStatusToggle(student.id, newStatus)}
                                    sx={{ width: '100%' }}
                                  >
                                    <ToggleButton
                                      value="Present"
                                      disabled={student.financial_hold}
                                      sx={{
                                        flex: 1,
                                        py: 0.8,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        '&.Mui-selected': { bgcolor: 'success.main', color: '#fff', '&:hover': { bgcolor: 'success.dark' } }
                                      }}
                                    >
                                      <PresentIcon sx={{ fontSize: 16, mr: 0.3 }} /> Présent
                                    </ToggleButton>
                                    <ToggleButton
                                      value="Late"
                                      disabled={student.financial_hold}
                                      sx={{
                                        flex: 1,
                                        py: 0.8,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        '&.Mui-selected': { bgcolor: 'warning.main', color: '#fff', '&:hover': { bgcolor: 'warning.dark' } }
                                      }}
                                    >
                                      <LateIcon sx={{ fontSize: 16, mr: 0.3 }} /> Retard
                                    </ToggleButton>
                                    <ToggleButton
                                      value="Absent"
                                      sx={{
                                        flex: 1,
                                        py: 0.8,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        '&.Mui-selected': { bgcolor: 'error.main', color: '#fff', '&:hover': { bgcolor: 'error.dark' } }
                                      }}
                                    >
                                      <AbsentIcon sx={{ fontSize: 16, mr: 0.3 }} /> Absent
                                    </ToggleButton>
                                    <ToggleButton
                                      value="Excused"
                                      sx={{
                                        flex: 1,
                                        py: 0.8,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        '&.Mui-selected': { bgcolor: 'info.main', color: '#fff', '&:hover': { bgcolor: 'info.dark' } }
                                      }}
                                    >
                                      <ExcusedIcon sx={{ fontSize: 16, mr: 0.3 }} /> Excusé
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                )}
                              </Box>

                              {/* Absence Justification Badge & Action */}
                              {(currentStatus === 'Absent' || currentStatus === 'Excused') && (
                                <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                  {att?.justified ? (
                                    <Tooltip title={att.justification_reason || t('academy.attendance.justified')}>
                                      <Chip
                                        icon={att.justification_doc_base64 || att.justification_doc_name ? <AttachFileIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
                                        label={att.justification_doc_name ? `${t('academy.attendance.justified')} (${att.justification_doc_name})` : (att.justification_reason ? `${t('academy.attendance.justified')}: ${att.justification_reason.substring(0, 15)}...` : t('academy.attendance.justified'))}
                                        color="info"
                                        size="small"
                                        onClick={() => handleOpenJustification(student, att)}
                                        sx={{ cursor: 'pointer', maxWidth: 200 }}
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="caption" color="error.main" sx={{ fontStyle: 'italic', fontWeight: 600 }}>
                                      {t('academy.attendance.unjustified')}
                                    </Typography>
                                  )}
                                  <Button
                                    size="small"
                                    variant={att?.justified ? "outlined" : "contained"}
                                    color={att?.justified ? "primary" : "warning"}
                                    startIcon={att?.justified ? <VisibilityIcon fontSize="small" /> : <EditNoteIcon fontSize="small" />}
                                    onClick={() => handleOpenJustification(student, att)}
                                    sx={{ fontSize: '0.72rem', py: 0.3 }}
                                  >
                                    {att?.justified ? t('academy.attendance.view_justification') : t('academy.attendance.justify')}
                                  </Button>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  /* Table Display */
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table>
                      <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Étudiant</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Assiduité</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Pointage Présence</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('academy.attendance.justification_details') || 'Justification'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleStudents.map(student => {
                          const att = attendances.find(a => a.session_id === currentSession.id && a.student_id === student.id);
                          const currentStatus = att?.status ?? null;
                          const passRate = calculateStudentPassRate(student.id);
                          const isSaving = savingStudentId === student.id;
                          const studentCode = student.code || student.STD_CODE || `STD-${String(student.id).padStart(4, '0')}`;

                          return (
                            <TableRow key={student.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar
                                    src={student.photo_base64}
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      bgcolor: student.financial_hold ? 'error.main' : 'primary.main',
                                      fontWeight: 700,
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    {!student.photo_base64 && `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={700}>
                                      {student.first_name} {student.last_name}
                                    </Typography>
                                    {student.financial_hold && (
                                      <Chip label={t('academy.students.hold_badge')} color="error" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                  {studentCode}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight={700} color={passRate >= 75 ? 'success.main' : 'error.main'}>
                                    {passRate}%
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={passRate}
                                    color={passRate >= 75 ? 'success' : 'error'}
                                    sx={{ width: 60, height: 4, borderRadius: 2 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                {isSaving ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <ToggleButtonGroup
                                    size="small"
                                    value={currentStatus}
                                    exclusive
                                    disabled={isSaving}
                                    onChange={(_, newStatus) => newStatus && handleStatusToggle(student.id, newStatus)}
                                  >
                                    <ToggleButton value="Present" disabled={student.financial_hold} sx={{ '&.Mui-selected': { bgcolor: 'success.main', color: '#fff' } }}>
                                      <PresentIcon sx={{ fontSize: 16, mr: 0.3 }} /> Présent
                                    </ToggleButton>
                                    <ToggleButton value="Late" disabled={student.financial_hold} sx={{ '&.Mui-selected': { bgcolor: 'warning.main', color: '#fff' } }}>
                                      <LateIcon sx={{ fontSize: 16, mr: 0.3 }} /> Retard
                                    </ToggleButton>
                                    <ToggleButton value="Absent" sx={{ '&.Mui-selected': { bgcolor: 'error.main', color: '#fff' } }}>
                                      <AbsentIcon sx={{ fontSize: 16, mr: 0.3 }} /> Absent
                                    </ToggleButton>
                                    <ToggleButton value="Excused" sx={{ '&.Mui-selected': { bgcolor: 'info.main', color: '#fff' } }}>
                                      <ExcusedIcon sx={{ fontSize: 16, mr: 0.3 }} /> Excusé
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                )}
                              </TableCell>
                              <TableCell>
                                {(currentStatus === 'Absent' || currentStatus === 'Excused') ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {att?.justified ? (
                                      <Tooltip title={att.justification_reason || t('academy.attendance.justified')}>
                                        <Chip
                                          icon={att.justification_doc_base64 || att.justification_doc_name ? <AttachFileIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
                                          label={att.justification_doc_name ? `${t('academy.attendance.justified')} (${att.justification_doc_name})` : (att.justification_reason ? `${t('academy.attendance.justified')}: ${att.justification_reason.substring(0, 15)}...` : t('academy.attendance.justified'))}
                                          color="info"
                                          size="small"
                                          onClick={() => handleOpenJustification(student, att)}
                                          sx={{ cursor: 'pointer', maxWidth: 180 }}
                                        />
                                      </Tooltip>
                                    ) : (
                                      <Chip
                                        label={t('academy.attendance.unjustified')}
                                        color="warning"
                                        variant="outlined"
                                        size="small"
                                      />
                                    )}
                                    <IconButton
                                      size="small"
                                      color={att?.justified ? "primary" : "warning"}
                                      title={att?.justified ? t('academy.attendance.view_justification') : t('academy.attendance.justify')}
                                      onClick={() => handleOpenJustification(student, att)}
                                    >
                                      {att?.justified ? <VisibilityIcon fontSize="small" /> : <EditNoteIcon fontSize="small" />}
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            ) : (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <AttendanceIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {t('academy.attendance.select_prompt') || 'Sélectionnez une session'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choisissez une session dans la colonne de gauche pour afficher et pointer la liste des étudiants.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* QR Attendance Scanner Modal */}
        <QrAttendanceScannerModal
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          currentSession={currentSession}
          onStudentScanned={handleStudentScanned}
          allStudents={students}
        />

        {/* Justification Details & Attachment Modal */}
        <Dialog
          open={justificationModalOpen}
          onClose={() => setJustificationModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            {t('academy.attendance.justification_details')}
          </DialogTitle>
          <DialogContent dividers>
            {selectedStudentForJustification && (
              <Box sx={{ mb: 2.5, p: 2, bgcolor: 'action.hover', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={selectedStudentForJustification.photo_base64}
                  sx={{ width: 48, height: 48, fontWeight: 700 }}
                >
                  {selectedStudentForJustification.first_name?.[0]}{selectedStudentForJustification.last_name?.[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {selectedStudentForJustification.first_name} {selectedStudentForJustification.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedStudentForJustification.code || `STD-${selectedStudentForJustification.id}`}
                    {currentGroup && ` • ${currentGroup.name}`}
                    {currentSession && ` • ${currentSession.date}`}
                  </Typography>
                </Box>
                {selectedAttendanceForJustification?.justification_date && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('academy.attendance.submission_date')}
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {new Date(selectedAttendanceForJustification.justification_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Reason Input */}
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('academy.attendance.reason_label')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              value={justificationReasonInput}
              onChange={(e) => setJustificationReasonInput(e.target.value)}
              placeholder={t('academy.attendance.reason_placeholder')}
              sx={{ mb: 3 }}
            />

            {/* Document Section */}
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('academy.attendance.doc_label')}
            </Typography>

            {justificationDocBase64Input || justificationDocNameInput ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: (justificationDocBase64Input.startsWith('data:image') || justificationDocNameInput.match(/\.(png|jpe?g|webp)$/i)) ? 2 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {justificationDocNameInput || 'document_justificatif'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.attendance.attached_file')}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {justificationDocBase64Input && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadDoc(justificationDocBase64Input, justificationDocNameInput)}
                      >
                        {t('academy.attendance.download_doc')}
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setJustificationDocBase64Input('');
                        setJustificationDocNameInput('');
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                {/* Inline image preview if image format */}
                {(justificationDocBase64Input.startsWith('data:image') || justificationDocNameInput.match(/\.(png|jpe?g|webp)$/i)) && (
                  <Box sx={{ textAlign: 'center', mt: 1, maxHeight: 200, overflow: 'hidden', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <img
                      src={justificationDocBase64Input.startsWith('data:') ? justificationDocBase64Input : `data:image/jpeg;base64,${justificationDocBase64Input}`}
                      alt={justificationDocNameInput}
                      style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Paper>
            ) : (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                {t('academy.attendance.no_doc')}
              </Alert>
            )}

            {/* File Upload Button */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              size="small"
            >
              {justificationDocBase64Input ? 'Remplacer le document' : 'Joindre un justificatif (PDF / Image)'}
              <input
                type="file"
                hidden
                accept="image/*,application/pdf"
                onChange={handleFileUpload}
              />
            </Button>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setJustificationModalOpen(false)} color="inherit" disabled={justificationSaving}>
              {t('btn.cancel') || 'Annuler'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveJustification}
              disabled={justificationSaving || !justificationReasonInput.trim()}
              startIcon={justificationSaving ? <CircularProgress size={18} /> : <PresentIcon />}
            >
              {t('academy.attendance.save_justification')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Mark All Present Dialog */}
        <Dialog
          open={confirmMarkAllOpen}
          onClose={() => setConfirmMarkAllOpen(false)}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('academy.attendance.mark_all_present') || 'Tout Marquer Présent'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('academy.attendance.mark_all_present_confirm') || 'Voulez-vous marquer tous les étudiants inscrits de cette session comme Présents ?'}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmMarkAllOpen(false)} color="inherit" disabled={batchLoading}>
              {t('btn.cancel') || 'Annuler'}
            </Button>
            <Button
              onClick={handleConfirmMarkAll}
              variant="contained"
              color="success"
              disabled={batchLoading}
              startIcon={batchLoading ? <CircularProgress size={18} /> : <DoneAllIcon />}
            >
              Confirmer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GenericPage>
  );
};

export default AttendancePage;
